import { MutableRefObject, useCallback, useMemo } from "react";
import {
	Gesture,
	GestureStateChangeEvent,
	GestureUpdateEvent,
	PanGestureChangeEventPayload,
	PanGestureHandlerEventPayload,
} from "react-native-gesture-handler";
import {
	SharedValue,
	runOnJS,
	useAnimatedStyle,
	useSharedValue,
	withSpring,
} from "react-native-reanimated";
import native from "src/constants/native";
import { GestureComposer, MainGesturePanListener, PositionLookupRef } from "../types";

const PAGE_THRESHOLD = 150;
const VELOCITY_MULTIPLIER = 0.3;
const useImageGalleryMainGesture = ({
	mainItemDimensions,
	positionLookup,
	curKey,
	isDragging,
	gestureComposer,
	processToPx,
	mainGesturePanListener,
}: {
	mainItemDimensions: {
		width: number;
		height: number;
	};
	positionLookup: PositionLookupRef;
	curKey: MutableRefObject<string>;
	isDragging: SharedValue<boolean>;
	gestureComposer: GestureComposer | undefined;
	processToPx: (toPx: number) => { newPx: number };
	mainGesturePanListener?: MainGesturePanListener;
}) => {
	const translateX = useSharedValue(0);
	const containerStyle = useAnimatedStyle(
		() => ({
			transform: [
				{
					translateX: -translateX.value,
				},
			],
		}),
		[translateX],
	);

	const startX = useSharedValue(0);
	const handlePanBegin = useCallback(
		(event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
			"worklet";
			startX.value = translateX.value;
			isDragging.value = false;
			mainGesturePanListener?.onBegin?.(event);
		},
		[startX, translateX, isDragging, mainGesturePanListener],
	);

	const handlePanChange = useCallback(
		(
			event: GestureUpdateEvent<PanGestureHandlerEventPayload & PanGestureChangeEventPayload>,
		) => {
			"worklet";
			translateX.value = startX.value - event.translationX;
			mainGesturePanListener?.onChange?.(event);
		},
		[translateX, startX, mainGesturePanListener],
	);

	const handlePageJS = useCallback(
		/**
		 * Number of pages. Negative is left, positive is right.
		 */
		(numPages: number, newTranslateX: number) => {
			const cur = positionLookup.current[curKey.current];
			if (!cur) throw new Error("cur is undefined");
			const curPx = cur.positionX;
			const toPx = curPx + numPages;

			const { newPx } = processToPx(toPx);
			// If there's desync between the new key and the new position, correct it
			// This happens at the end points
			if (newTranslateX !== newPx * mainItemDimensions.width) {
				translateX.value = withSpring(newPx * mainItemDimensions.width, {
					overshootClamping: true,
				});
			}
		},
		[curKey, mainItemDimensions.width, positionLookup, processToPx, translateX],
	);

	const handlePage = useCallback(
		/**
		 * Number of pages. Negative is left, positive is right.
		 */
		(
			numPages: number,
			{
				velocity = 0,
			}: {
				velocity?: number;
			} = {},
		) => {
			"worklet";

			const newTranslateX =
				Math.floor(startX.value / mainItemDimensions.width + numPages) *
				mainItemDimensions.width;
			// Aim to run this animation in the UI thread so it's unaffected by JS lag (causes stuttering)
			translateX.value = withSpring(newTranslateX, {
				velocity: -velocity,
				overshootClamping: true,
			});
			runOnJS(handlePageJS)(numPages, newTranslateX);
		},
		[mainItemDimensions, startX, translateX, handlePageJS],
	);

	const handlePanEnd = useCallback(
		(event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
			"worklet";
			const numPages = (() => {
				if (event.translationX + event.velocityX * VELOCITY_MULTIPLIER < -PAGE_THRESHOLD) {
					return 1;
				}
				if (event.translationX + event.velocityX * VELOCITY_MULTIPLIER > PAGE_THRESHOLD) {
					return -1;
				}
				return 0;
			})();
			handlePage(numPages, { velocity: event.velocityX });
			mainGesturePanListener?.onEnd?.(event);
		},
		[handlePage, mainGesturePanListener],
	);

	const gesture = useMemo(() => {
		const pan = Gesture.Pan()
			// This seems to be the distance required for screen navigation for native to kick in first
			.minDistance(native ? 40 : 10)
			.maxPointers(1)
			.onBegin(handlePanBegin)
			.onChange(handlePanChange)
			.onEnd(handlePanEnd);
		if (gestureComposer) {
			return gestureComposer(pan);
		}
		return pan;
	}, [handlePanBegin, handlePanChange, handlePanEnd, gestureComposer]);
	return { gesture, containerStyle, translateX, handlePage };
};

export default useImageGalleryMainGesture;
